import { useState } from 'react';
import { Bug as BugIcon } from 'lucide-react';
import { useTicketCounts } from './useTicketCounts';
import { BugReportModal } from './BugReportModal';
import { useBugAccess, type BugAccess, type BugAccessDeps } from './useBugAccess';
import { playerSurfaceActive } from '../../lib/api/customerContext';

// ─────────────────────────────────────────────────────────────────────
// BugReportButton — the floating button that opens the bug-report modal and
// shows live counts of active bugs/feature-requests. Extracted verbatim from
// the original artifact (legacy/App.jsx). Markup is preserved exactly.
//
// SECRET HANDLING: no secrets here — <BugReportModal> reads the bug-report
// config (BUG_REPORT_EMAIL from VITE_* env) directly from ./config. This button
// threads no secret props. (Web3Forms is retired, #1142.)
// ─────────────────────────────────────────────────────────────────────

/**
 * The floating button's dress, shared with the dev menu so the two cannot
 * drift: a compact dark pill when something is open, and a small light disc
 * when nothing is. The idle disc used to be faded to 40 % opacity, which on
 * the #809 palette left it nearly invisible; it is now solid with a strong
 * border and a shadow, so it can be found without hovering, and stays small
 * so it is still out of the way.
 */
export function floatingButtonClass(total: number): string {
  const base = 'flex items-center gap-1.5 rounded-full transition-all';
  return total > 0
    ? `${base} px-2.5 py-1.5 bg-ink text-white shadow-md hover:bg-ink-2`
    : `${base} p-2 bg-white text-ink-2 border border-line-strong shadow-sm hover:text-ink hover:shadow-md`;
}

export interface BugReportButtonProps {
  currentView: any;
  currentContext: any;
  onOpenArchive?: any;
  /** Injectable for tests: how the button learns what it may offer. */
  accessDeps?: BugAccessDeps;
}

/**
 * The floating button, deciding for itself what it may offer (owner decision
 * 2026-10-22): see `useBugAccess`. `DevMenuButton` already knows, and renders
 * `BugReportFloatingButton` with its answer instead of asking twice.
 */
export function BugReportButton({ accessDeps, ...props }: BugReportButtonProps) {
  const access = useBugAccess(accessDeps);
  return <BugReportFloatingButton {...props} access={access} />;
}

export interface BugReportFloatingButtonProps {
  currentView: any;
  currentContext: any;
  onOpenArchive?: any;
  access: BugAccess;
}

/**
 * WHO SEES WHAT (owner decision 2026-10-22):
 *
 *   bug administrator     the button, the counts and the archive link;
 *   anybody else, open    the button, which opens the report form only;
 *   anybody else, closed  nothing.
 */
export function BugReportFloatingButton({
  currentView,
  currentContext,
  onOpenArchive,
  access,
}: BugReportFloatingButtonProps) {
  const [showModal, setShowModal] = useState(false);
  // Counts of active bugs/feature-requests (open, in_progress — paused + closed
  // are not counted), re-read when the modal opens or closes (a new report may
  // have been added) and every 2 seconds otherwise (in case the user
  // deletes/marks from the archive page). Through the SHARED poller (#878):
  // the DEV menu shows the same badges, and it renders this button inside
  // itself, so a copy of the effect here fetched the whole list twice.
  //
  // ADMINISTRATORS ONLY: a reporter is submit-only, so nothing is polled.
  const { bugs: bugCount, features: featureCount } = useTicketCounts(showModal, access.isAdmin);

  const total = bugCount + featureCount;

  if (!access.canReport) return null;

  return (
    <>
      {/* SMALL ON PURPOSE: one icon and the total of open bugs + suggestions.
          With nothing open it fades almost out of the way until hovered. */}
      {/* Above the «new version» banner while it shows (#1019, W43 retro):
          `DevMenuButton` renders this plain button when there are no personas,
          so it needs the same offset the dev menu has. */}
      <button
        onClick={() => setShowModal(true)}
        aria-label="Meld bug eller forslag"
        className={`fixed right-4 z-40 ${floatingButtonClass(total)}`}
        style={{ bottom: 'calc(1rem + var(--new-build-banner-h, 0px))' }}
        title={
          access.isAdmin && total > 0
            ? `${bugCount} bug${bugCount === 1 ? '' : 's'}, ${featureCount} forslag — klikk for å rapportere eller se arkiv`
            : 'Rapporter bug eller foreslå ny funksjon'
        }
      >
        <BugIcon size={13} strokeWidth={2.5} />
        {total > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white font-mono text-[10px] font-bold leading-[18px] text-center">
            {total}
          </span>
        )}
      </button>

      {showModal && (
        <BugReportModal
          currentView={currentView}
          currentContext={currentContext}
          onClose={() => setShowModal(false)}
          onOpenArchive={access.isAdmin ? onOpenArchive : undefined}
          mode={playerSurfaceActive() ? 'member' : 'staff'}
        />
      )}
    </>
  );
}
